import { Box, Button, Icon, Text, type BackgroundToken } from "@salestrak/ui";
import {
  HeaderLabel,
  IconTile,
  PanelCard,
  ScreenHeader,
  StatCard,
  StatusDot,
  TableHeaderRow,
  TableRow,
} from "./admin-ui";
import type {
  CronRunStatus,
  CronSchedulerMode,
  ScheduledTaskRow,
  ScheduledTaskRunRow,
} from "#app/lib/data";
import { formatDateTime } from "#app/lib/utils/format-date";
import { formatDuration } from "#app/lib/utils/format-duration";
import { formatUntil } from "#app/lib/utils/format-relative-date";

const RUN_STATUS = {
  completed: { color: "success", label: "Completed" },
  failed: { color: "destructive", label: "Failed" },
} as const satisfies Record<
  CronRunStatus,
  { color: BackgroundToken; label: string }
>;

export function ScheduledTasksScreen({
  tasks,
  runs,
  mode,
  registered,
  loading,
  error,
  lastChecked,
  refresh,
  failedCount,
  overdueCount,
}: {
  tasks: ScheduledTaskRow[];
  runs: ScheduledTaskRunRow[];
  mode: CronSchedulerMode | null;
  registered: boolean;
  loading: boolean;
  error: string | null;
  lastChecked: string | null;
  refresh: () => void;
  failedCount: number;
  overdueCount: number;
}) {
  const schedulerValue =
    mode == null ? "—" : mode === "disabled" ? "Off" : registered ? "On" : "On*";

  return (
    <Box fill="flex" overflow="auto">
      <ScreenHeader
        title="Scheduled tasks"
        meta={lastChecked ? `Checked ${lastChecked}` : undefined}
        actions={
          <Button variant="outline" size="sm" disabled={loading} onClick={refresh}>
            <Icon name="refresh-cw" size="sm" color="muted" />
            <Text>{loading ? "Refreshing…" : "Refresh"}</Text>
          </Button>
        }
      />

      <Box direction="row" gap="sm" paddingX="lg" paddingTop="sm">
        <StatCard label="Tasks" value={`${tasks.length}`} />
        <StatCard
          label="Scheduler"
          value={schedulerValue}
          color={mode === "disabled" ? "warning" : mode == null ? undefined : "success"}
        />
        <StatCard
          label="Overdue"
          value={`${overdueCount}`}
          color={overdueCount ? "warning" : undefined}
        />
        <StatCard label="Retained runs" value={`${runs.length}`} />
        <StatCard
          label="Failures"
          value={`${failedCount}`}
          color={failedCount ? "destructive" : undefined}
        />
      </Box>

      {/* An unreadable scheduler must never render as an idle-but-healthy one:
          every count above is zero either way, so the failure is stated. */}
      {error ? (
        <Box direction="row" align="center" gap="xs" paddingX="lg" paddingTop="sm">
          <Icon name="alert-circle" size="xs" color="destructive" />
          <Text size="xs" variant="destructive">
            Could not read scheduled tasks — the figures above are not current.{" "}
            {error}
          </Text>
        </Box>
      ) : null}

      {mode === "disabled" ? (
        <Box direction="row" align="center" gap="xs" paddingX="lg" paddingTop="sm">
          <Icon name="alert-triangle" size="xs" color="warning" />
          <Text size="xs" variant="warning">
            Scheduling is off on this deployment (no Redis) — the sweeps do not
            run at all, and no history exists.
          </Text>
        </Box>
      ) : null}

      <Box paddingX="lg" paddingTop="md">
        <PanelCard>
          <TableHeaderRow>
            <HeaderLabel fill>Task</HeaderLabel>
            <HeaderLabel width={140}>Schedule (UTC)</HeaderLabel>
            <HeaderLabel fill>Last run</HeaderLabel>
            <HeaderLabel fill>Next run</HeaderLabel>
          </TableHeaderRow>

          {tasks.map((t, index) => (
            <TableRow key={t.name} last={index === tasks.length - 1}>
              <Box fill="flex" direction="row" align="center" gap="sm">
                <IconTile icon="calendar-clock" />
                <Box direction="column">
                  <Text size="sm" weight="medium">
                    {t.label}
                  </Text>
                  <Text size="xs" variant="muted">
                    {t.description}
                  </Text>
                </Box>
              </Box>
              <Box width={140}>
                <Text size="sm" mono variant="info">
                  {t.schedule}
                </Text>
              </Box>
              <Box fill="flex" direction="row" align="center" gap="xs">
                {t.lastRun ? (
                  <>
                    <StatusDot background={RUN_STATUS[t.lastRun.status].color} />
                    <Text size="sm" variant="muted" mono>
                      {formatDateTime(t.lastRun.startedAt)} ·{" "}
                      {formatDuration(t.lastRun.durationMs) ?? "—"}
                    </Text>
                  </>
                ) : (
                  <Text size="sm" variant="muted">
                    No retained runs
                  </Text>
                )}
              </Box>
              <Box fill="flex" direction="row" align="baseline" gap="xs">
                {t.nextRunAt ? (
                  <>
                    <Text
                      size="sm"
                      weight="medium"
                      mono
                      variant={t.overdue ? "warning" : undefined}
                    >
                      {formatUntil(t.nextRunAt) ?? "—"}
                    </Text>
                    <Text size="xs" variant="muted" mono>
                      {formatDateTime(t.nextRunAt)}
                    </Text>
                  </>
                ) : (
                  <Text size="sm" variant="muted">
                    Not scheduled
                  </Text>
                )}
              </Box>
            </TableRow>
          ))}
        </PanelCard>
      </Box>

      <Box paddingX="lg" paddingTop="md" paddingBottom="xl">
        <Box paddingBottom="xs">
          <Text size="sm" weight="semibold">
            Run history
          </Text>
        </Box>
        <PanelCard>
          <TableHeaderRow>
            <HeaderLabel width={140}>Task</HeaderLabel>
            <HeaderLabel width={110}>Status</HeaderLabel>
            <HeaderLabel width={150}>Started</HeaderLabel>
            <HeaderLabel width={90} align="right">
              Duration
            </HeaderLabel>
            <HeaderLabel width={70} align="right">
              Attempts
            </HeaderLabel>
            <HeaderLabel fill>Outcome</HeaderLabel>
          </TableHeaderRow>

          {!loading && runs.length === 0 ? (
            <Box paddingX="md" paddingY="lg" align="center">
              <Text size="sm" variant="muted">
                No retained runs
              </Text>
            </Box>
          ) : null}

          {runs.map((r, index) => (
            <TableRow
              key={r.jobId ?? `${r.name}-${index}`}
              last={index === runs.length - 1}
            >
              <Box width={140}>
                <Text size="sm" weight="medium">
                  {r.label}
                </Text>
              </Box>
              <Box width={110} direction="row" align="center" gap="xs">
                <StatusDot background={RUN_STATUS[r.status].color} />
                <Text size="sm" weight="medium" variant={RUN_STATUS[r.status].color}>
                  {RUN_STATUS[r.status].label}
                </Text>
              </Box>
              <Box width={150}>
                <Text size="sm" variant="muted" mono>
                  {formatDateTime(r.startedAt)}
                </Text>
              </Box>
              <Box width={90} align="end">
                <Text size="sm" mono>
                  {formatDuration(r.durationMs) ?? "—"}
                </Text>
              </Box>
              <Box width={70} align="end">
                <Text
                  size="sm"
                  mono
                  variant={r.attemptsMade > 1 ? "warning" : "muted"}
                >
                  {r.attemptsMade}
                </Text>
              </Box>
              <Box fill="flex">
                <Text
                  size="sm"
                  mono
                  variant={r.status === "failed" ? "destructive" : "muted"}
                  truncate={1}
                >
                  {(r.status === "failed" ? r.error : r.resultSummary) ?? "—"}
                </Text>
              </Box>
            </TableRow>
          ))}
        </PanelCard>

        <Box direction="row" align="center" gap="xs" paddingTop="sm">
          <Icon name="info" size="xs" color="muted" />
          <Text size="xs" variant="muted">
            Read-only. History is what the queue retains; the sweeps are
            single-producer, so there is deliberately no manual trigger.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
